import type { RefCallback, ReactNode } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { cn } from "@/modern/utils";
import type { FileViewerMode } from "../types";
import { MetaRail } from "./MetaRail";
import { PromotePanel } from "./promote/PromotePanel";
import { ExtractionPanel } from "./extraction/ExtractionPanel";
import { SummarySharePanel } from "./SummarySharePanel";
import { AdjustPointsPanel } from "./AdjustPointsPanel";

type RailMode = Exclude<FileViewerMode, "annotate">;

interface FileDetailRailProps {
  mode: RailMode;
  analyzeDockRef: RefCallback<HTMLDivElement>;
  /** Kept for callers; panels close themselves back to Details. */
  onBackToDetails: () => void;
}

/**
 * Expanded rail footprint as a flex item — percentage tracks the shell; min-width keeps forms usable.
 * The inner card mirrors this with container-query units (see FILE_DETAIL_RAIL_CARD_WIDTH_CLS) so it
 * keeps a fixed size while the wrapper's width animates open/closed.
 */
export const FILE_DETAIL_RAIL_EXPANDED_CLS = "w-[30%] min-w-[26rem]";

/** Same footprint as FILE_DETAIL_RAIL_EXPANDED_CLS, resolved against the `@container` row. */
const FILE_DETAIL_RAIL_CARD_WIDTH_CLS = "w-[max(30cqw,26rem)]";

/** Must match the `gap` the media stage and rail previously shared (gap-5). */
const RAIL_GAP = "1.25rem";

/**
 * Soft bubbly card — same footprint for every surface so mode switches never shift layout.
 * Shown/hidden from FileDetailModal (fully unmounted when collapsed).
 */
export const FILE_DETAIL_RAIL_SHELL_CLS = cn(
  "relative flex h-full min-h-0 shrink-0 flex-col self-stretch",
  "overflow-hidden rounded-[2rem] bg-card p-5",
  "border border-border/50 shadow-sm",
);

/** @deprecated Prefer FILE_DETAIL_RAIL_SHELL_CLS with motion width. */
export const FILE_DETAIL_RAIL_SHELL_EXPANDED_CLS = cn(
  FILE_DETAIL_RAIL_SHELL_CLS,
  FILE_DETAIL_RAIL_EXPANDED_CLS,
);

export const FILE_DETAIL_RAIL_PANEL_ID = "file-detail-rail-panel";

/** Motion: drawer-range slide under 300ms (jony_motion modal_or_drawer). */
const RAIL_EASE = [0.23, 1, 0.32, 1] as const;
const RAIL_DURATION_S = 0.28;

function RailSurface({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">{children}</div>
  );
}

/**
 * Right rail card: Details / Summary & Share / Promote / Extract / Analyze / Adjust Points.
 * Surfaces crossfade in-place.
 *
 * Expand/collapse is two layers so nothing reflows mid-animation:
 *  - Outer wrapper is the flex item; its `width`/`min-width`/`margin-left` animate 0 ⇄ final,
 *    so the media stage grows/shrinks continuously instead of jumping.
 *  - Inner card is absolutely positioned at a fixed container-query width and slides/fades,
 *    so its content never re-wraps while the wrapper is mid-transition.
 * Requires the parent flex row to be a `@container` (inline-size).
 */
export function FileDetailRail({ mode, analyzeDockRef }: FileDetailRailProps) {
  const reduceMotion = useReducedMotion();
  const duration = reduceMotion ? 0 : RAIL_DURATION_S;
  const transition = { duration, ease: RAIL_EASE };

  return (
    <motion.div
      className="relative h-full shrink-0 self-stretch"
      initial={{ width: 0, minWidth: 0, marginLeft: 0 }}
      animate={{ width: "30%", minWidth: "26rem", marginLeft: RAIL_GAP }}
      exit={{ width: 0, minWidth: 0, marginLeft: 0 }}
      transition={transition}
    >
      <motion.aside
        id={FILE_DETAIL_RAIL_PANEL_ID}
        className={cn(
          FILE_DETAIL_RAIL_SHELL_CLS,
          FILE_DETAIL_RAIL_CARD_WIDTH_CLS,
          "absolute inset-y-0 left-0",
        )}
        aria-label="File panel"
        initial={reduceMotion ? false : { opacity: 0, x: 24 }}
        animate={{ opacity: 1, x: 0 }}
        exit={reduceMotion ? { opacity: 0 } : { opacity: 0, x: 24 }}
        transition={transition}
      >
        <div className="relative min-h-0 flex-1 overflow-hidden">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={mode}
              className="absolute inset-0 flex min-h-0 flex-col overflow-hidden"
              initial={reduceMotion ? false : { opacity: 0, transform: "translateY(6px)" }}
              animate={{ opacity: 1, transform: "translateY(0px)" }}
              exit={
                reduceMotion
                  ? { opacity: 0 }
                  : { opacity: 0, transform: "translateY(-4px)" }
              }
              transition={{
                duration: reduceMotion ? 0 : 0.16,
                ease: [0.22, 1, 0.36, 1],
              }}
            >
              {mode === "default" ? (
                <RailSurface>
                  <MetaRail />
                </RailSurface>
              ) : null}
              {mode === "summaryShare" ? (
                <RailSurface>
                  <SummarySharePanel />
                </RailSurface>
              ) : null}
              {mode === "promote" ? (
                <RailSurface>
                  <PromotePanel />
                </RailSurface>
              ) : null}
              {mode === "extract" ? (
                <RailSurface>
                  <ExtractionPanel />
                </RailSurface>
              ) : null}
              {mode === "analyze" ? (
                <RailSurface>
                  <div ref={analyzeDockRef} className="flex h-full min-h-0 flex-col" />
                </RailSurface>
              ) : null}
              {mode === "adjustPoints" ? (
                <RailSurface>
                  <AdjustPointsPanel />
                </RailSurface>
              ) : null}
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.aside>
    </motion.div>
  );
}
